// A table as an Excel workbook (.xlsx) of one sheet, written here instead of with a
// library: the admin's webpack 4 reads few of today's, and the server's build has
// little memory to spare. An .xlsx is a zip of XML files; these are stored in it
// uncompressed, as a zip may.

// Excel refuses a file with the control characters XML has no place for
const escapeXml = (text) =>
  String(text)
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0b\x0c\x0e-\x1f]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// The column letters of Excel: A … Z, AA …
const columnOf = (index) =>
  (index >= 26 ? columnOf(Math.floor(index / 26) - 1) : "") + String.fromCharCode(65 + (index % 26));

// The cell formats of styles.xml, in the order a cell names them by (s="…"): the
// number format, font, fill and border of each. 3 is Excel's own "#,##0", 164 the
// one with ₮ after it. The total row is bold, with a line above it.
const FORMATS = [
  ["text", 0, 0, 0, 0],
  ["count", 3, 0, 0, 0],
  ["money", 164, 0, 0, 0],
  ["title", 0, 2, 0, 0],
  ["head", 0, 1, 2, 1],
  ["total text", 0, 1, 0, 2],
  ["total count", 3, 1, 0, 2],
  ["total money", 164, 1, 0, 2],
];
const styleOf = (name) => FORMATS.findIndex((format) => format[0] === name);

const STYLES =
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<numFmts count="1"><numFmt numFmtId="164" formatCode="#,##0 &quot;₮&quot;"/></numFmts>' +
  '<fonts count="3"><font><sz val="11"/><name val="Calibri"/></font>' +
  '<font><b/><sz val="11"/><name val="Calibri"/></font>' +
  '<font><b/><sz val="13"/><name val="Calibri"/></font></fonts>' +
  '<fills count="3"><fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FFF3F4F6"/><bgColor indexed="64"/></patternFill></fill></fills>' +
  '<borders count="3"><border><left/><right/><top/><bottom/><diagonal/></border>' +
  '<border><left/><right/><top/><bottom style="thin"><color rgb="FFD1D5DB"/></bottom><diagonal/></border>' +
  '<border><left/><right/><top style="thin"><color rgb="FF9CA3AF"/></top><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  `<cellXfs count="${FORMATS.length}">` +
  FORMATS.map(
    ([name, numFmt, font, fill, border]) =>
      `<xf numFmtId="${numFmt}" fontId="${font}" fillId="${fill}" borderId="${border}" xfId="0"` +
      ' applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"' +
      (name === "head" ? ' applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>' : "/>")
  ).join("") +
  "</cellXfs>" +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  "</styleSheet>";

const cellOf = (value, ref, style) =>
  typeof value === "number"
    ? `<c r="${ref}" s="${styleOf(style)}"><v>${value}</v></c>`
    : `<c r="${ref}" s="${styleOf(style)}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(value ?? "")}</t></is></c>`;

const rowOf = (cells, index) => `<row r="${index + 1}">${cells.join("")}</row>`;

// The title, the head of the columns below it, which stays in view as the rows
// scroll, the rows and their total
const sheetOf = ({ title, columns, rows, total }) => {
  const formatOf = (index) => columns[index].format || "text";
  // The values of a row below the head, the first of them line 3
  const valuesOf = (values, line, prefix) =>
    values.map((value, index) =>
      cellOf(value, `${columnOf(index)}${line + 3}`, prefix + formatOf(index))
    );
  const lines = [
    [cellOf(title, "A1", "title")],
    columns.map((column, index) => cellOf(column.label, `${columnOf(index)}2`, "head")),
    ...rows.map((row, line) => valuesOf(row, line, "")),
    ...(total ? [valuesOf(total, rows.length, "total ")] : []),
  ];
  return (
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    '<sheetViews><sheetView workbookViewId="0">' +
    '<pane ySplit="2" topLeftCell="A3" activePane="bottomLeft" state="frozen"/>' +
    "</sheetView></sheetViews>" +
    "<cols>" +
    columns
      .map((column, index) => `<col min="${index + 1}" max="${index + 1}" width="${column.width || 14}" customWidth="1"/>`)
      .join("") +
    "</cols>" +
    `<sheetData>${lines.map(rowOf).join("")}</sheetData>` +
    "</worksheet>"
  );
};

// A sheet's name is at most 31 characters, and none of these
const sheetNameOf = (name) => name.replace(/[\\/?*[\]:]/g, " ").slice(0, 31) || "Sheet1";

const filesOf = (sheet) => ({
  "[Content_Types].xml":
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    "</Types>",
  "_rels/.rels":
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
    "</Relationships>",
  "xl/workbook.xml":
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<sheets><sheet name="${escapeXml(sheetNameOf(sheet.name))}" sheetId="1" r:id="rId1"/></sheets>` +
    "</workbook>",
  "xl/_rels/workbook.xml.rels":
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
    '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
    "</Relationships>",
  "xl/styles.xml": STYLES,
  "xl/worksheets/sheet1.xml": sheetOf(sheet),
});

const CRC_TABLE = [...Array(256).keys()].map((n) => {
  let c = n;
  for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  return c >>> 0;
});
const crcOf = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
};

// Little endian numbers of 2 and 4 bytes, as the zip format has them
const bytesOf = (...fields) => {
  const bytes = [];
  fields.forEach(([value, size]) => {
    for (let i = 0; i < size; i += 1) bytes.push((value >>> (8 * i)) & 0xff);
  });
  return Uint8Array.from(bytes);
};

// Every file dated 1980-01-01, the first day a zip can say
const DOS_DATE = (1 << 5) | 1;

const zipOf = (files) => {
  const encoder = new TextEncoder();
  const parts = [];
  const directory = [];
  let offset = 0;
  Object.entries(files).forEach(([name, text]) => {
    const path = encoder.encode(name);
    const data = encoder.encode('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' + text);
    const crc = crcOf(data);
    const sizes = [[crc, 4], [data.length, 4], [data.length, 4], [path.length, 2]];
    const header = bytesOf([0x04034b50, 4], [20, 2], [0, 2], [0, 2], [0, 2], [DOS_DATE, 2], ...sizes, [0, 2]);
    // The same, with what made it, and where in the zip its file starts
    const entry = bytesOf(
      [0x02014b50, 4], [20, 2], [20, 2], [0, 2], [0, 2], [0, 2], [DOS_DATE, 2], ...sizes,
      [0, 2], [0, 2], [0, 2], [0, 2], [0, 4], [offset, 4]
    );
    directory.push(entry, path);
    parts.push(header, path, data);
    offset += header.length + path.length + data.length;
  });
  const size = directory.reduce((sum, part) => sum + part.length, 0);
  const count = Object.keys(files).length;
  const end = bytesOf([0x06054b50, 4], [0, 2], [0, 2], [count, 2], [count, 2], [size, 4], [offset, 4], [0, 2]);
  return new Blob([...parts, ...directory, end], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

// Downloads the sheet as fileName. The sheet is { name, title, columns, rows, total }:
// columns as { label, width, format }, format "text", "count" or "money"; rows and
// total as the values of each column, numbers as numbers.
export const saveXlsx = (fileName, sheet) => {
  const url = URL.createObjectURL(zipOf(filesOf(sheet)));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};
